import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Cookies from "js-cookie";
import { useAuth } from "./useAuth.js";
import { normalizeDispatchVehicleRecord } from "./dispatchOccupancyHelpers.js";

const VehicleWebSocketContext = createContext(null);

const RECONNECT_DELAY_MS = 3000;
const LOADING_TIMEOUT_MS = 10000;

const ACTIVE_TRIP_STATUSES = new Set([
  "ongoing",
  "completed",
  "at-final-stop",
]);

const hasValidCoordinates = (item) => {
  const coordinates = item?.location?.coordinates;
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 2) {
    return false;
  }

  const [longitude, latitude] = coordinates;
  return (
    typeof longitude === "number" &&
    typeof latitude === "number" &&
    !Number.isNaN(longitude) &&
    !Number.isNaN(latitude)
  );
};

export const filterVehicleDispatchData = (items, { requireCoordinates = false } = {}) => {
  const now = Date.now();
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1,
  );

  return items.filter((item) => {
    const itemDate = new Date(item.updatedAt || item.createdAt || now);
    const isToday = itemDate >= startOfToday && itemDate < endOfToday;
    if (!isToday) return false;

    const status = item.status?.toLowerCase();
    if (!ACTIVE_TRIP_STATUSES.has(status)) return false;

    if (requireCoordinates && !hasValidCoordinates(item)) return false;

    return true;
  });
};

const applyVehicleUpdates = (prevData, updates) => {
  const dataMap = new Map(prevData.map((item) => [item.documentId, item]));

  for (const update of updates) {
    const { type, documentId, data } = update;

    if (type === "added" || type === "modified") {
      dataMap.set(documentId, normalizeDispatchVehicleRecord({ documentId, ...data }));
    }

    if (type === "removed") {
      dataMap.delete(documentId);
    }
  }

  return filterVehicleDispatchData(Array.from(dataMap.values()));
};

const resolveInstitutionId = (privilege) =>
  privilege?.institutionId ?? Cookies.get("selectedInstitutionId") ?? null;

export function VehicleWebSocketProvider({ children }) {
  const { privilege } = useAuth();
  const [institutionId, setInstitutionId] = useState(() =>
    resolveInstitutionId(privilege),
  );

  const [dispatchData, setDispatchData] = useState([]);
  const [loading, setLoading] = useState(true);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);
  const loadingRef = useRef(true);
  const institutionIdRef = useRef(institutionId);
  const connectWebSocketRef = useRef(null);

  institutionIdRef.current = institutionId;

  useEffect(() => {
    const nextInstitutionId = resolveInstitutionId(privilege);
    setInstitutionId((prev) =>
      prev === nextInstitutionId ? prev : nextInstitutionId,
    );
  }, [privilege?.institutionId]);

  useEffect(() => {
    const handleCookieUpdate = () => {
      const nextInstitutionId =
        Cookies.get("selectedInstitutionId") ??
        privilege?.institutionId ??
        null;

      setInstitutionId((prev) =>
        prev === nextInstitutionId ? prev : nextInstitutionId,
      );
    };

    window.addEventListener("cookieUpdate", handleCookieUpdate);

    return () => {
      window.removeEventListener("cookieUpdate", handleCookieUpdate);
    };
  }, [privilege?.institutionId]);

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeWebSocket = useCallback(() => {
    clearReconnectTimeout();

    const ws = wsRef.current;
    if (!ws) return;

    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;

    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }

    wsRef.current = null;
  }, [clearReconnectTimeout]);

  const finishLoading = useCallback(() => {
    if (!loadingRef.current) return;

    loadingRef.current = false;
    setLoading(false);
    clearLoadingTimeout();
  }, [clearLoadingTimeout]);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimeout();
    reconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && institutionIdRef.current) {
        connectWebSocketRef.current?.();
      }
    }, RECONNECT_DELAY_MS);
  }, [clearReconnectTimeout]);

  const connectWebSocket = useCallback(() => {
    const currentInstitutionId = institutionIdRef.current;
    if (!currentInstitutionId) {
      finishLoading();
      return;
    }

    closeWebSocket();

    let ws;
    try {
      ws = new WebSocket(
        `${process.env.REACT_APP_WEBSOCKET}?x-api-key=${process.env.REACT_APP_API_KEY}`,
      );
    } catch (error) {
      console.error("WebSocket connection failed to establish:", error);
      scheduleReconnect();
      return;
    }

    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) return;

      const subscriptionMessage = {
        type: "subscribe",
        institutionId: institutionIdRef.current,
      };

      console.log("[VehicleWebSocket] Connected, subscribing:", subscriptionMessage);
      ws.send(JSON.stringify(subscriptionMessage));

      clearLoadingTimeout();
      loadingTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          finishLoading();
        }
      }, LOADING_TIMEOUT_MS);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("[VehicleWebSocket] Message received:", message);

        if (message.type === "location_update" && Array.isArray(message.updates)) {
          if (message.updates.length > 0) {
            setDispatchData((prevData) => {
              const nextData = applyVehicleUpdates(prevData, message.updates);
              console.log("[VehicleWebSocket] Dispatch data updated:", {
                previousCount: prevData.length,
                nextCount: nextData.length,
                updates: message.updates,
                dispatchData: nextData,
              });
              return nextData;
            });
          } else {
            console.log("[VehicleWebSocket] Empty location_update (no active trips)");
          }
          finishLoading();
        } else {
          console.log("[VehicleWebSocket] Unhandled message type:", message.type);
        }
      } catch (err) {
        console.error("[VehicleWebSocket] Error parsing message:", err, event.data);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    ws.onclose = () => {
      if (wsRef.current === ws) {
        wsRef.current = null;
      }

      if (isMountedRef.current) {
        console.warn("WebSocket closed. Attempting to reconnect...");
        scheduleReconnect();
      }
    };
  }, [
    clearLoadingTimeout,
    closeWebSocket,
    finishLoading,
    scheduleReconnect,
  ]);

  connectWebSocketRef.current = connectWebSocket;

  useEffect(() => {
    isMountedRef.current = true;

    if (!institutionId) {
      setDispatchData([]);
      loadingRef.current = false;
      setLoading(false);
      closeWebSocket();
      clearLoadingTimeout();

      return () => {
        isMountedRef.current = false;
        clearLoadingTimeout();
        closeWebSocket();
      };
    }

    loadingRef.current = true;
    setLoading(true);
    setDispatchData([]);
    connectWebSocket();

    return () => {
      isMountedRef.current = false;
      clearLoadingTimeout();
      closeWebSocket();
    };
  }, [
    institutionId,
    connectWebSocket,
    closeWebSocket,
    clearLoadingTimeout,
  ]);

  return (
    <VehicleWebSocketContext.Provider value={{ dispatchData, loading }}>
      {children}
    </VehicleWebSocketContext.Provider>
  );
}

export function useVehicleWebSocket(options = {}) {
  const context = useContext(VehicleWebSocketContext);

  if (!context) {
    throw new Error("useVehicleWebSocket must be used within VehicleWebSocketProvider");
  }

  const { requireCoordinates = false } = options;
  const { dispatchData, loading } = context;

  const filteredData = useMemo(() => {
    if (!requireCoordinates) return dispatchData;
    return filterVehicleDispatchData(dispatchData, { requireCoordinates: true });
  }, [dispatchData, requireCoordinates]);

  return {
    dispatchData: filteredData,
    loading,
  };
}
